/**
 * Maps human-readable keys to translation IDs for the /tools/mafld-calculator
 * SEO landing page. New content (not from Framer XLIFF), so keys carry the
 * `mafld_` prefix directly.
 *
 * The page is gated to CH + DE (`page:tools/mafld-calculator`), in all three
 * Swiss locales and in German for the DE market, so `de-DE` is held to full
 * parity with this namespace while de-AT, en-NL and nl still exempt it in
 * `SKIP_NAMESPACES_PER_LOCALE` (scripts/lint-mirror-locales.ts). Nothing in
 * the MAFLD definition is calibrated per country, so the German rollout was
 * the copy plus one thing the FIB-4 and sMAF-5 pages did not need: this page
 * reads a lipid, and German labs report lipids in mg/dL, so the triglyceride
 * field asks that market in mg/dL and offers the switch back to mmol/L (hence
 * the four unit keys below). The definition's one population caveat is
 * ethnicity rather than market, and it is the same caveat in every country
 * (see lib/mafld.ts and `mafld_limit4Body`).
 *
 * No key in this namespace takes an ICU placeholder. That is deliberate, and
 * it costs more here than on the other calculator pages because this one has
 * five `as const` registries in `lib/mafld.ts` holding these keys as literals
 * (sex, steatosis answers, index bands, metabolic criteria, routes,
 * outcomes). A single placeholder-bearing key anywhere in the namespace would
 * make next-intl's typed `t()` demand a `values` argument at every one of
 * those call sites: the TS2554 trap documented in the BMI calculator's
 * `bmi.ts`. Where a threshold has to appear inside a sentence it is written
 * into the sentence.
 */
export const mafldCalculatorKeys = {
  // Metadata
  metaTitle: "mafld_metaTitle",
  metaDescription: "mafld_metaDescription",

  // Hero
  heading: "mafld_heading", // "MAFLD calculator"
  subheading: "mafld_subheading",

  // --- Calculator widget ---
  calcTitle: "mafld_calcTitle",
  calcSexLabel: "mafld_calcSexLabel",
  calcSexFemale: "mafld_calcSexFemale",
  calcSexMale: "mafld_calcSexMale",
  calcHeightLabel: "mafld_calcHeightLabel",
  calcWeightLabel: "mafld_calcWeightLabel",
  calcWaistLabel: "mafld_calcWaistLabel",
  calcBodyHint: "mafld_calcBodyHint",
  calcWaistHint: "mafld_calcWaistHint",
  calcTriglyceridesLabel: "mafld_calcTriglyceridesLabel",
  calcGgtLabel: "mafld_calcGgtLabel",
  calcBloodHint: "mafld_calcBloodHint",
  calcTriglyceridesHint: "mafld_calcTriglyceridesHint",
  // The mg/dL twin of the hint above, and the switch's own label. Rendered
  // only where `feature:tools-mgdl-lipids` allows the market, but present in
  // every catalog: en.json is what types `t()`, so a key missing there is a
  // compile error at the call site rather than a missing string.
  calcTriglyceridesHintMgDl: "mafld_calcTriglyceridesHintMgDl",
  calcTriglyceridesUnitLabel: "mafld_calcTriglyceridesUnitLabel",
  calcDiabetesLabel: "mafld_calcDiabetesLabel",
  calcDiabetesYes: "mafld_calcDiabetesYes",
  calcDiabetesNo: "mafld_calcDiabetesNo",
  calcDiabetesHint: "mafld_calcDiabetesHint",
  calcSteatosisLabel: "mafld_calcSteatosisLabel",
  calcSteatosisConfirmed: "mafld_calcSteatosisConfirmed",
  calcSteatosisExcluded: "mafld_calcSteatosisExcluded",
  calcSteatosisUnknown: "mafld_calcSteatosisUnknown",
  calcSteatosisHint: "mafld_calcSteatosisHint",
  calcMarkersLabel: "mafld_calcMarkersLabel",
  calcMarkersHint: "mafld_calcMarkersHint",

  // Units, rendered as input suffixes
  calcUnitCm: "mafld_calcUnitCm", // "cm"
  calcUnitKg: "mafld_calcUnitKg", // "kg"
  calcUnitMmol: "mafld_calcUnitMmol", // "mmol/L"
  calcUnitMgDl: "mafld_calcUnitMgDl", // "mg/dL"
  calcUnitUl: "mafld_calcUnitUl", // "U/L"

  calcSubmit: "mafld_calcSubmit",
  calcReset: "mafld_calcReset",

  // Result panel
  calcResultLabel: "mafld_calcResultLabel", // "The verdict"
  calcFliLabel: "mafld_calcFliLabel", // "Fatty Liver Index"
  calcBmiLabel: "mafld_calcBmiLabel", // "Your BMI:"
  calcEmptyState: "mafld_calcEmptyState",
  calcOutOfRange: "mafld_calcOutOfRange",
  calcBodyOutOfRange: "mafld_calcBodyOutOfRange",
  calcTriglyceridesUnit: "mafld_calcTriglyceridesUnit",
  // The mirror image of the message above, for a mmol/L figure typed while
  // mg/dL is the selected unit. See `looksLikeMmolPerLEntry`
  // (lib/lipid-units.ts) for why that direction needs its own detection.
  calcTriglyceridesMmolValue: "mafld_calcTriglyceridesMmolValue",
  calcThresholdsLabel: "mafld_calcThresholdsLabel",
  calcScaleCaption: "mafld_calcScaleCaption",
  calcNextLabel: "mafld_calcNextLabel", // "What to do with this"
  calcLimbsLabel: "mafld_calcLimbsLabel", // "The metabolic half, met through"
  calcNoLimbsNote: "mafld_calcNoLimbsNote",
  calcCriteriaMetLabel: "mafld_calcCriteriaMetLabel",
  calcCriteriaNoneNote: "mafld_calcCriteriaNoneNote",
  calcLeanUnansweredNote: "mafld_calcLeanUnansweredNote",
  calcSteatosisScanNote: "mafld_calcSteatosisScanNote",
  calcEligibilityNote: "mafld_calcEligibilityNote",
  calcDisclaimer: "mafld_calcDisclaimer",

  // Quiz hand-off, shown in the result panel once Calculate has run
  quizHeading: "mafld_quizHeading",
  quizBody: "mafld_quizBody",
  quizCta: "mafld_quizCta",

  // --- The four outcomes (result chip + the outcome reference list) ---
  outcomeMet: "mafld_outcomeMet",
  outcomeMetMeaning: "mafld_outcomeMetMeaning",
  outcomeMetNext: "mafld_outcomeMetNext",
  outcomeIndeterminate: "mafld_outcomeIndeterminate",
  outcomeIndeterminateMeaning: "mafld_outcomeIndeterminateMeaning",
  outcomeIndeterminateNext: "mafld_outcomeIndeterminateNext",
  outcomeNoMetabolic: "mafld_outcomeNoMetabolic",
  outcomeNoMetabolicMeaning: "mafld_outcomeNoMetabolicMeaning",
  outcomeNoMetabolicNext: "mafld_outcomeNoMetabolicNext",
  outcomeNoSteatosis: "mafld_outcomeNoSteatosis",
  outcomeNoSteatosisMeaning: "mafld_outcomeNoSteatosisMeaning",
  outcomeNoSteatosisNext: "mafld_outcomeNoSteatosisNext",

  // --- The three metabolic routes ---
  limbOverweight: "mafld_limbOverweight",
  limbOverweightThr: "mafld_limbOverweightThr",
  limbOverweightBody: "mafld_limbOverweightBody",
  limbDiabetes: "mafld_limbDiabetes",
  limbDiabetesThr: "mafld_limbDiabetesThr",
  limbDiabetesBody: "mafld_limbDiabetesBody",
  limbLean: "mafld_limbLean",
  limbLeanThr: "mafld_limbLeanThr",
  limbLeanBody: "mafld_limbLeanBody",

  // --- The six metabolic criteria of route (c) ---
  critWaist: "mafld_critWaist",
  critWaistThr: "mafld_critWaistThr",
  critBloodPressure: "mafld_critBloodPressure",
  critBloodPressureThr: "mafld_critBloodPressureThr",
  critTriglycerides: "mafld_critTriglycerides",
  critTriglyceridesThr: "mafld_critTriglyceridesThr",
  critHdl: "mafld_critHdl",
  critHdlThr: "mafld_critHdlThr",
  critPrediabetes: "mafld_critPrediabetes",
  critPrediabetesThr: "mafld_critPrediabetesThr",
  critCrp: "mafld_critCrp",
  critCrpThr: "mafld_critCrpThr",

  // --- The three Fatty Liver Index bands ---
  fliBandUnlikely: "mafld_fliBandUnlikely",
  fliBandIndeterminate: "mafld_fliBandIndeterminate",
  fliBandLikely: "mafld_fliBandLikely",
  fliMeaningUnlikely: "mafld_fliMeaningUnlikely",
  fliMeaningIndeterminate: "mafld_fliMeaningIndeterminate",
  fliMeaningLikely: "mafld_fliMeaningLikely",
  fliThrUnlikely: "mafld_fliThrUnlikely",
  fliThrIndeterminate: "mafld_fliThrIndeterminate",
  fliThrLikely: "mafld_fliThrLikely",
  fliAccUnlikely: "mafld_fliAccUnlikely",
  fliAccIndeterminate: "mafld_fliAccIndeterminate",
  fliAccLikely: "mafld_fliAccLikely",

  // What MAFLD is
  whatLabel: "mafld_whatLabel",
  whatHeading: "mafld_whatHeading",
  whatP1: "mafld_whatP1",
  whatP2: "mafld_whatP2",
  whatP3: "mafld_whatP3",

  // How the diagnosis is made: the two halves, the routes, the criteria table
  howLabel: "mafld_howLabel",
  howHeading: "mafld_howHeading",
  howIntro: "mafld_howIntro",
  howRoutesTitle: "mafld_howRoutesTitle",
  criteriaHeading: "mafld_criteriaHeading",
  criteriaIntro: "mafld_criteriaIntro",
  criteriaColName: "mafld_criteriaColName",
  criteriaColThreshold: "mafld_criteriaColThreshold",
  criteriaNote: "mafld_criteriaNote",

  // The Fatty Liver Index section
  fliLabel: "mafld_fliLabel",
  fliHeading: "mafld_fliHeading",
  fliIntro: "mafld_fliIntro",
  fliTableColBand: "mafld_fliTableColBand",
  fliTableColIndex: "mafld_fliTableColIndex",
  fliTableColMeaning: "mafld_fliTableColMeaning",
  fliTableColAccuracy: "mafld_fliTableColAccuracy",
  fliTableNote: "mafld_fliTableNote",

  // What goes into it
  inputsLabel: "mafld_inputsLabel",
  inputsHeading: "mafld_inputsHeading",
  inputsIntro: "mafld_inputsIntro",
  input1Title: "mafld_input1Title",
  input1Body: "mafld_input1Body",
  input2Title: "mafld_input2Title",
  input2Body: "mafld_input2Body",
  input3Title: "mafld_input3Title",
  input3Body: "mafld_input3Body",
  input4Title: "mafld_input4Title",
  input4Body: "mafld_input4Body",
  input5Title: "mafld_input5Title",
  input5Body: "mafld_input5Body",
  input6Title: "mafld_input6Title",
  input6Body: "mafld_input6Body",

  // MAFLD against NAFLD and MASLD, the naming question every reader has
  namesHeading: "mafld_namesHeading",
  namesP1: "mafld_namesP1",
  namesP2: "mafld_namesP2",
  namesP3: "mafld_namesP3",

  // Who it is for, who it is not for
  whoHeading: "mafld_whoHeading",
  whoIntro: "mafld_whoIntro",
  whoForTitle: "mafld_whoForTitle",
  whoFor1: "mafld_whoFor1",
  whoFor2: "mafld_whoFor2",
  whoFor3: "mafld_whoFor3",
  whoNotForTitle: "mafld_whoNotForTitle",
  whoNotFor1: "mafld_whoNotFor1",
  whoNotFor2: "mafld_whoNotFor2",
  whoNotFor3: "mafld_whoNotFor3",
  whoNotFor4: "mafld_whoNotFor4",
  whoNote: "mafld_whoNote",

  // Units, the half of this page that is market-specific
  unitsHeading: "mafld_unitsHeading",
  unitsP1: "mafld_unitsP1",
  unitsP2: "mafld_unitsP2",
  unitsTipTitle: "mafld_unitsTipTitle",
  unitsTipBody: "mafld_unitsTipBody",

  // Limitations
  limitsLabel: "mafld_limitsLabel",
  limitsHeading: "mafld_limitsHeading",
  limitsIntro: "mafld_limitsIntro",
  limit1Title: "mafld_limit1Title",
  limit1Body: "mafld_limit1Body",
  limit2Title: "mafld_limit2Title",
  limit2Body: "mafld_limit2Body",
  limit3Title: "mafld_limit3Title",
  limit3Body: "mafld_limit3Body",
  limit4Title: "mafld_limit4Title",
  limit4Body: "mafld_limit4Body",

  // Membership section
  memLabel: "mafld_memLabel",
  memHeading: "mafld_memHeading",
  memBody: "mafld_memBody",
  memFeature1: "mafld_memFeature1",
  memFeature2: "mafld_memFeature2",
  memFeature3: "mafld_memFeature3",
  memFeature4: "mafld_memFeature4",
  memFeature5: "mafld_memFeature5",
  memCtaPrimary: "mafld_memCtaPrimary",
  memCtaSecondary: "mafld_memCtaSecondary",
  memFootnote: "mafld_memFootnote",

  // FAQ fallback (used until page-tagged FAQs exist in the CMS)
  faqHeading: "mafld_faqHeading",
  faqQ1: "mafld_faqQ1",
  faqA1: "mafld_faqA1",
  faqQ2: "mafld_faqQ2",
  faqA2: "mafld_faqA2",
  faqQ3: "mafld_faqQ3",
  faqA3: "mafld_faqA3",
  faqQ4: "mafld_faqQ4",
  faqA4: "mafld_faqA4",
  faqQ5: "mafld_faqQ5",
  faqA5: "mafld_faqA5",
  faqQ6: "mafld_faqQ6",
  faqA6: "mafld_faqA6",

  // Closing CTA
  ctaHeading: "mafld_ctaHeading",
  ctaBody: "mafld_ctaBody",
  ctaPrimary: "mafld_ctaPrimary",
  ctaSecondary: "mafld_ctaSecondary",

  // Legal / medical
  sourceLabel: "mafld_sourceLabel",
  sourceBody: "mafld_sourceBody",
  disclaimer: "mafld_disclaimer",
} as const
